import { Fragment, useContext } from "react";
import "./App.css";
import "react-quill/dist/quill.snow.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import EmailBuilder from "./EmailBuilder";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SwipeableTemporaryDrawer from "./Demo";
import MiniDrawer from "./SlideHeader";
import DragDroppable from "./DragDroppable";
import FormBuilder from "./formbuilder/FormBuilder";
import Login from "./login/Login";
import { USER_ROLES } from "./utils/constants";
import { QueryClient, QueryClientProvider } from "react-query";
import { ThemeProvider } from "@mui/material";
import { ExpoTheme } from "./themes/ExpoTheme";
import MainDash from "./dashboard/MainDash";
import { UserContext } from "./context/UserProvider";
import SharableOnsite from "./dashboard/onsitemodule/SharableOnsite";
import { ExportProvider } from "./context/ExportProvider";
import DocumentMeta from "./dashboard/settings/DocumentMeta";
import { ImageUploadProvider } from "./context/ImageUploadContext";
import MuPb from "./widgets/MuPb";
import { isLoggedIn } from "./utils/utils2";
import AppLogout from "./login/AppLogout";

import EmailTemplateEditor from "./emails/EmailTemplateEditor";
import EmailTemplateCards from "./emails/ChooseEmailTemplates";
import EmailBlockEditor from "./emails/EmailBlockEditor";
import ChangePasswordForm from "./dashboard/profile/PasswordUpdate";
import AdminProfilePage from "./dashboard/profile/AdminProfile";
import OesDataForm from "./formbuilder/oesform/OesDataForm";
import RegistrationSettings from "./dashboard/registration/RegistrationSettings";
import RegistrationPreview from "./dashboard/registration/RegistrationPreview";
import RegistrationTermsConditions from "./dashboard/registration/RegistrationTermsConditions";
import RegBadgePreview from "./dashboard/registration/RegBadgePreview";
import OesFormBuilder from "./formbuilder/oesform/OesFromBuilder";
import MobileAppForm from "./dashboard/mobileapps/MobileAppForm";
import BadgeBuilder from "./dashboard/badge/BadgeBuilder";
import NotifyExhibitorsUpdateProfile from "./dashboard/pages/NotifyExhibitorsUpdateProfile";
import GeneralFloorPlan from "./dashboard/floorplan/GeneralFloorPlan";
import Visitors from "./components/visitors/Visitors";
import Exhibitors from "./dashboard/exhibitors/Exhibitors";
import ChooseAddOptions from "./dashboard/exhibitors/ChooseAddOptions";
import AddExhibitorForm from "./dashboard/exhibitors/AddExhibitorForm";
import ExhibitorExcelReader from "./dashboard/exhibitors/ExhibitorExcelReader";
import AddHall from "./dashboard/floorplan/AddHall";
import EventsList from "./dashboard/events/EventsList";
import ExportDataPage from "./dashboard/exportdata/ExportDataPage";
import UploadCategoryPage from "./dashboard/productcategory/UploadCategoryPage";
import VisitorDetail from "./components/visitors/VisitorDetail";
import ExhibitorDetail from "./dashboard/exhibitors/ExhibitorDetail";
import { OesFormBuilderDemo } from "./formbuilder/oesform/OesFormBuilderDemo";
import AddEventForm from "./dashboard/events/AddEventForm";
import SuperPreview from "./pages/SuperPreview";
import EventDetail from "./pages/EventDetail";
import FileStorage from "./dashboard/filestorage/FileStorage";
import UploadFiles from "./dashboard/filestorage/UploadFiles";
import AddStand from "./dashboard/floorplan/AddStand";
import HallPlan from "./dashboard/floorplan/HallPlan";
import UpdateExcelReader from "./dashboard/exhibitors/UpdateExcelReader";
import AddTradeDelegate from "./dashboard/exhibitors/AddTradeDelegate";
import AddServiceCodes from "./dashboard/exhibitors/AddServiceCodes";
import DelegatesList from "./dashboard/pages/DelegatesList";
import EventSettings from "./dashboard/pages/EventSettings";
import GtagConfigForm from "./dashboard/pages/GtagConfigForm";
import SettingsPage from "./dashboard/Settings";
import AppMetadataEditor from "./dashboard/settings/AppMetaDataEditor";
import VisitorAnalyticsDashboard from "./components/visitors/VisitorAnalyticsDashboard";
import MetaPixelConfigForm from "./dashboard/pages/MetaPixelConfigForm";
import RegistrationAnalyticsTracker from "./dashboard/settings/RegistrationAnalyticsTracker";
import Dashboard from "./dashboard/Dashboard";
import SuperSettings from "./dashboard/SuperSettings";
import OesExplorer from "./dashboard/OesExplorer";
import CreateForm from "./dashboard/pages/CreateForm";
import ManageDelegates from "./components/visitors/ManageDelegates";
import VisitorProfileDetail from "./components/visitors/VisitorProfileDetail";
import OnsiteContainer from "./dashboard/onsitemodule/OnsiteContainer";
import OnsiteLinks from "./dashboard/onsitemodule/OnsiteLinks";
import ServiceCodesList from "./dashboard/exhibitors/ServiceCodesList";
import { DialogProvider } from "./components/dialogs/DialogProvider";
import ExhibitorsScannedAnalytics from "./pages/ExhibitorsScannedAnalytics";
import EmbeddedExternalPage from "./dashboard/EmbeddedExternalPage";
import RegistrationComparisonAnalytics from "./dashboard/analytics/RegistrationComparisonAnalytics";
import { isAdminRestrictedPath } from "./utils/accessControl";

const queryClient = new QueryClient();

const AdminAccessGuard = ({ role, children }) => {
  const location = useLocation();

  if (
    role === USER_ROLES.ADMIN &&
    isAdminRestrictedPath(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { user, loading } = useContext(UserContext);
  if (loading) {
    return <MuPb />;
  }

  return (
    <ThemeProvider theme={ExpoTheme}>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <Fragment>
            <ImageUploadProvider>
              <ExportProvider>
                <BrowserRouter>
                  <AdminAccessGuard role={user?.role}>
                    <DocumentMeta />
                    <ToastContainer autoClose={1500} />
                    <Routes>
                    {/* ======================================= */}
                    {/* 1. PUBLIC / STANDALONE ROUTES (NO SIDEBAR) */}
                    {/* ======================================= */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/logout" element={<AppLogout />} />
                    <Route path="/email" element={<EmailBuilder />} />
                    <Route
                      path="/demo"
                      element={<SwipeableTemporaryDrawer />}
                    />
                    <Route path="/slide-draw" element={<MiniDrawer />} />
                    <Route path="/test-dnd" element={<DragDroppable />} />
                    <Route
                      path="/onsite-module/:id"
                      element={<SharableOnsite />}
                    />

                    <Route path="/onsite/*" element={<OnsiteContainer />} />
                    {/* Add any other routes here that SHOULD NOT have the sidebar */}

                    {/* ======================================= */}
                    {/* 2. LOGGED-IN ROUTES (HAS SIDEBAR & APPBAR)*/}
                    {/* ======================================= */}
                    <Route
                      path="/"
                      element={
                        isLoggedIn() ? (
                          <MainDash />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    >
                      {/* 👇 ALL ROUTES BELOW RENDER INSIDE THE MAINDASH <Outlet /> */}

                      {/* The default route when navigating to "/" */}
                      <Route index element={<Dashboard />} />
                      <Route path="/onsite-links" element={<OnsiteLinks />} />

                      {/* Notice the missing "/" at the start of the path strings below */}
                      <Route path="profile" element={<AdminProfilePage />} />
                      <Route
                        path="change-password"
                        element={<ChangePasswordForm />}
                      />
                      <Route path="badge-builder" element={<BadgeBuilder />} />
                      <Route path="mobile-apps" element={<MobileAppForm />} />
                      <Route
                        path="floor-plan-creator"
                        element={<GeneralFloorPlan />}
                      />
                      <Route path="fp-add-hall" element={<AddHall />} />
                      <Route path="fp-add-stand/:id" element={<AddStand />} />
                      <Route path="hall-plan/:id" element={<HallPlan />} />
                      <Route path="add-event" element={<AddEventForm />} />
                      <Route path="events-list" element={<EventsList />} />
                      <Route path="export-data" element={<ExportDataPage />} />
                      <Route
                        path="product-category"
                        element={<UploadCategoryPage />}
                      />
                      <Route
                        path="registration-settings"
                        element={<RegistrationSettings />}
                      />
                      <Route
                        path="visitors-list"
                        element={<Visitors type={USER_ROLES.VISITOR} />}
                      />
                      <Route
                        path="tradedelegates-list"
                        element={<Visitors type={USER_ROLES.TRADE_DELEGATE} />}
                      />
                      <Route
                        path="students-list"
                        element={<Visitors type={USER_ROLES.STUDENT} />}
                      />
                      <Route
                        path="special-invitees"
                        element={<Visitors type={USER_ROLES.SPECIAL_INVITEE} />}
                      />
                      <Route path="visitor/:id" element={<VisitorDetail />} />
                      <Route
                        path="exhibitor/:id"
                        element={<ExhibitorDetail />}
                      />
                      <Route path="exhibitors-list" element={<Exhibitors />} />
                      <Route
                        path="visitors-count-analytics"
                        element={<VisitorAnalyticsDashboard />}
                      />
                      <Route
                        path="registration-analytics"
                        element={<RegistrationComparisonAnalytics />}
                      />
                      <Route
                        path="ufi-analytics"
                        element={<Navigate to="/registration-analytics" replace />}
                      />
                      <Route
                        path="catalogue"
                        element={
                          <EmbeddedExternalPage
                            title="Catalogue"
                            url="https://eventcatalogue.imtma.in/admin/login"
                          />
                        }
                      />
                      <Route
                        path="registration-page-preview"
                        element={<RegistrationPreview />}
                      />
                      <Route
                        path="registration-terms-conditions"
                        element={<RegistrationTermsConditions />}
                      />
                      <Route
                        path="registration-badge-preview"
                        element={<RegBadgePreview />}
                      />
                      <Route
                        path="exhibitors-scanned-analytics"
                        element={<ExhibitorsScannedAnalytics />}
                      />
                      <Route
                        path="add-service-codes"
                        element={<AddServiceCodes />}
                      />
                      <Route
                        path="service-codes-list"
                        element={<ServiceCodesList />}
                      />
                      <Route
                        path="oes-form-builder/:id"
                        element={<OesFormBuilderDemo />}
                      />
                      <Route path="file-storage" element={<FileStorage />} />
                      <Route path="upload-files" element={<UploadFiles />} />
                      <Route
                        path="oes-form-builder-2/:id"
                        element={<OesFormBuilder />}
                      />
                      <Route
                        path="notify-exhibitors-update-profile"
                        element={<NotifyExhibitorsUpdateProfile />}
                      />
                      <Route
                        path="add-exhibitor"
                        element={<ChooseAddOptions />}
                      />
                      <Route
                        path="add-exhibitor-form"
                        element={<AddExhibitorForm />}
                      />
                      <Route
                        path="registration-analytics-tracker/:type"
                        element={<RegistrationAnalyticsTracker />}
                      />
                      <Route
                        path="edit-exhibitor-form/:id"
                        element={<AddExhibitorForm />}
                      />
                      <Route
                        path="import-exhibitor-form"
                        element={<ExhibitorExcelReader />}
                      />
                      <Route
                        path="update-exhibitor-excel"
                        element={<UpdateExcelReader />}
                      />
                      <Route
                        path="banner-logo-settings"
                        element={<EventSettings />}
                      />
                      <Route
                        path="delegates-list"
                        element={<DelegatesList />}
                      />
                      <Route
                        path="manage-delegates"
                        element={<ManageDelegates />}
                      />
                      <Route
                        path="visitor-view/:id"
                        element={<VisitorProfileDetail />}
                      />
                      <Route
                        path="add-trade-delegate-excel"
                        element={<AddTradeDelegate />}
                      />
                      <Route path="update-gtag" element={<GtagConfigForm />} />
                      <Route
                        path="update-meta-pixel-tag"
                        element={<MetaPixelConfigForm />}
                      />
                      <Route path="settings" element={<SettingsPage />} />
                      <Route
                        path="super-settings"
                        element={<SuperSettings />}
                      />
                      <Route path="form-links" element={<CreateForm />} />
                      <Route path="oes-explorer" element={<OesExplorer />} />
                      <Route
                        path="meta-data-editor"
                        element={<AppMetadataEditor />}
                      />
                      <Route
                        path="email-template"
                        element={<EmailTemplateEditor />}
                      />
                      <Route path="oes-forms" element={<OesDataForm />} />
                      <Route
                        path="registration-form-builder"
                        element={<FormBuilder />}
                      />
                      <Route
                        path="super-manage-preview"
                        element={<SuperPreview />}
                      />
                      <Route
                        path="super-event-preview/:id"
                        element={<EventDetail />}
                      />
                      <Route
                        path="choose-email-template"
                        element={<EmailTemplateCards />}
                      />
                      <Route
                        path="email-block-editor"
                        element={<EmailBlockEditor />}
                      />
                    </Route>
                    {/* ======================================= */}
                    {/* END LOGGED-IN ROUTES */}
                    {/* ======================================= */}
                    </Routes>
                  </AdminAccessGuard>
                </BrowserRouter>
              </ExportProvider>
            </ImageUploadProvider>
          </Fragment>
        </DialogProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
